import { getRegion } from '@/helpers/getRegion';
import { getCurrentUser } from '@/hooks/query/useQueryUser';
import { isOnPrem } from '@/utils/buildProfile';
import {
  getTranscriptionPreviewParam,
  handleDomainRewrite,
  TRANSCRIPTION_PREVIEW_HOSTNAME,
} from '@/utils/endpoints';
import { applyXClientHeaders } from '@/utils/http/clientIdentityHeaders';
import { attachConfirmedDesktopOfflineRequestShortCircuit } from '@/utils/http/desktopOfflineNetwork';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { redactHeadersInPlace } from '@/utils/http/redactHeaders';
import { applyTracingHeaders } from '@/utils/http/tracing/tracingHeaders';
import { logger } from '@/utils/logger/logger';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { Region } from '@heidi/schema/ml-scribe';
import {
  DiarisationStatusSchema,
  type DiarisedSegmentSchema,
  type DiarisedSpeakerSchema,
  DiarisedTranscriptSchema,
  SpeakerRoleSchema,
  WordSchema,
} from '@heidi/session/data';
import { getWorkspaceIdHeader } from '@heidi/workspace/data';
import axios, { AxiosError } from 'axios';
import pRetry, { AbortError } from 'p-retry';
import { z } from 'zod';
export {
  DiarisedSegmentSchema,
  DiarisedSpeakerSchema,
  DiarisedTranscriptSchema,
  SpeakerRoleSchema,
  WordSchema,
} from '@heidi/session/data';

export const AudioInfoSchema = z.object({
  mime_type: z.string().nullable(),
  size: z.number().nullable(),
  duration: z.number().optional(),
  url: z.string().optional(),
});

export const RecordingIssueTypeSchema = z.enum([
  'MISSING_CHUNKS',
  'RETRIED_CHUNKS',
  'DUPLICATE_CHUNKS',
]);

export const RecordingIssueSchema = z.object({
  type: RecordingIssueTypeSchema,
  message: z.string(),
});

export const TranscriptSegmentSchema = z.object({
  segment_id: z.number(),
  text: z.string(),
  words: z.array(WordSchema),
  start_time: z.number().nullable().optional(),
  end_time: z.number().nullable().optional(),
  speaker_id: z.string(),
});

export const TranscriptChunkSchema = z.object({
  index: z.number(),
  text: z.string(),
  confidence: z.number(),
  model: z.string(),
  audio_info: AudioInfoSchema,
  created_at: z.string().datetime({ offset: true }),
  timestamp_ms: z.number(),
  timestamp_cumulative_ms: z.number(),
  diarised_transcript: DiarisedTranscriptSchema.nullable().optional(),
  diarisation_status: DiarisationStatusSchema.nullish(),
});

const TranscriptRecordingSchema = z.object({
  recording_id: z.string(),
  start_date: z.string().datetime({ offset: true }),
  end_date: z.string().datetime({ offset: true }).optional(),
  last_chunk_index: z.number().optional(),
  chunks: z.array(TranscriptChunkSchema),
  issues: z.array(RecordingIssueSchema),
});

export const TranscriptResponseSchema = z.object({
  session_id: z.string(),
  recordings: z.array(TranscriptRecordingSchema),
});

export const TranscriptionResultSchema = z.object({
  text: z.string(),
  model: z.string(),
  confidence: z.number().nullable().optional(),
  segments: z.array(TranscriptSegmentSchema).optional(),
  diarised_transcript: DiarisedTranscriptSchema.nullable().optional(),
  processed: z.string().nullable().optional(),
  post_processing_model: z.string().nullable().optional(),
  routing_group_name: z.string().nullable().optional(),
  languages: z.array(z.string()).nullable().optional(),
});

export const SpeakerLabelsPatchResponseSchema = z.object({
  speaker_labels: z.record(z.string(), z.union([z.string(), z.null()])),
});

export type SpeakerLabelsPatchResponse = z.infer<typeof SpeakerLabelsPatchResponseSchema>;

export const SpeakerRolesPatchResponseSchema = z.object({
  speaker_roles: z.record(z.string(), SpeakerRoleSchema.nullable()),
});

export type SpeakerRolesPatchResponse = z.infer<typeof SpeakerRolesPatchResponseSchema>;

export const SegmentReassignmentSchema = z
  .object({
    recording_id: z.string().min(1),
    chunk_index: z.number().int().nonnegative(),
    segment_id: z.number().int().nonnegative(),
    // Required with no default: after a split the same segment_id names
    // several rows, and defaulting 0 would silently move the head row.
    segment_offset: z.number().int().nonnegative(),
    speaker_id: z.string().min(1),
    // Character offsets into that row's text. Omit both to move the whole
    // row; a later slice can send both. One without the other is invalid.
    start: z.number().int().nonnegative().optional(),
    end: z.number().int().positive().optional(),
  })
  .superRefine((row, ctx) => {
    if ((row.start == null) !== (row.end == null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'start and end must be given together',
      });
    }
    if (row.start != null && row.end != null && row.end <= row.start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'end must be greater than start',
      });
    }
  });

/** One persisted speaker boundary in stored-segment coordinates (end exclusive), not GET row shape. */
export const SpeakerSegmentBoundarySchema = z
  .object({
    recording_id: z.string().min(1),
    chunk_index: z.number().int().nonnegative(),
    segment_id: z.number().int().nonnegative(),
    speaker_id: z.string().min(1),
    start_offset: z.number().int().nonnegative(),
    end_offset: z.number().int().positive(),
  })
  .superRefine((row, ctx) => {
    if (row.end_offset <= row.start_offset) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'end_offset must be greater than start_offset',
      });
    }
  });

export const SegmentSpeakersPatchResponseSchema = z.object({
  // Boundaries stored for every chunk the request touched. Callers re-GET the transcript for rows.
  speaker_boundaries: z.array(SpeakerSegmentBoundarySchema),
});

export type SpeakerRole = z.infer<typeof SpeakerRoleSchema>;
export type SegmentReassignment = z.infer<typeof SegmentReassignmentSchema>;
export type SpeakerSegmentBoundary = z.infer<typeof SpeakerSegmentBoundarySchema>;
export type SegmentSpeakersPatchResponse = z.infer<
  typeof SegmentSpeakersPatchResponseSchema
>;

export const SpeakerResponseSchema = z.object({
  speaker_id: z.string().min(1),
  label: z.string().nullable(),
  role: SpeakerRoleSchema.nullable(),
});

export type SpeakerResponse = z.infer<typeof SpeakerResponseSchema>;
export type CreateSpeakerBody = {
  label?: string | null;
  role?: SpeakerRole | null;
};

export type TranscriptResponse = z.infer<typeof TranscriptResponseSchema>;
export type TranscriptRecording = z.infer<typeof TranscriptRecordingSchema>;
export type TranscriptChunk = z.infer<typeof TranscriptChunkSchema>;
export type RecordingIssue = z.infer<typeof RecordingIssueSchema>;
export type AudioInfo = z.infer<typeof AudioInfoSchema>;
export type RecordingIssueType = z.infer<typeof RecordingIssueTypeSchema>;
export type TranscriptionResult = z.infer<typeof TranscriptionResultSchema>;
export type DiarisedSpeaker = z.infer<typeof DiarisedSpeakerSchema>;
export type DiarisedSegment = z.infer<typeof DiarisedSegmentSchema>;
export type DiarisedTranscript = z.infer<typeof DiarisedTranscriptSchema>;
export type DiarisationStatus = z.infer<typeof DiarisationStatusSchema>;
export type Word = z.infer<typeof WordSchema>;
export type TranscriptSegment = z.infer<typeof TranscriptSegmentSchema>;

export const t11nClient = axios.create();
export const t11nClientV2 = axios.create();

const regionToApiSubdomain: Record<Region, string> = {
  [Region.AU]: 'api.aws-aue1',
  [Region.CA]: 'api.aws-cac1',
  [Region.EU]: 'api.aws-euc1',
  [Region.CH]: 'api.aws-euc2',
  [Region.UK]: 'api.aws-euw2',
  [Region.US]: 'api.aws-use1',
  [Region.AU_AZ1]: 'au-az1.api',
  [Region.JP_AZ1]: 'jp-az1.api',
  [Region.QAT_AZ1]: 'qat-az1.api',
  [Region.UAE_AZ1]: 'uae-az1.api',
  [Region.US_AZ1]: 'us-az1.api',
  [Region.SE_AZ1]: 'se-az1.api',
};

const STAGING_REGIONS = new Set<Region>([Region.AU, Region.US, Region.AU_AZ1]);

const REGIONS_WITHOUT_CHUNK_TICKETS = new Set<Region>([
  Region.AU_AZ1,
  Region.JP_AZ1,
  Region.QAT_AZ1,
  Region.UAE_AZ1,
  Region.US_AZ1,
  Region.SE_AZ1,
]);

const transcriptionBaseUrl = process.env.NEXT_PUBLIC_TRANSCRIPTION_SERVICE_BASE_URL;

const HOSTNAME_SUFFIX_API_PROD_HEIDI = 'api.prod.heidihealth.com';

/** Azure hosts resolve as `{region}.api.prod…`; public routes use `{region}.api…` without `.prod`. AWS stays `api.aws-*.prod…` (no `.api.prod.` segment). Only the parsed hostname is rewritten so path/query cannot be corrupted. */
function normalizeAzureApiProdHost(url: string): string {
  try {
    const parsed = new URL(url);
    const { hostname } = parsed;
    if (!hostname.endsWith(HOSTNAME_SUFFIX_API_PROD_HEIDI)) {
      return url;
    }
    parsed.hostname = hostname.replace(
      /\.api\.prod\.heidihealth\.com$/i,
      '.api.heidihealth.com'
    );
    return parsed.href;
  } catch {
    return url;
  }
}

/** Shared ticket listener on the resolved transcription host (local port: 5411). */
export function getChunkTicketsBaseUrl(): string | null {
  if (isOnPrem) return null;

  const hostname = typeof window === 'undefined' ? '' : (window.location.hostname ?? '');
  if (/^pr-\d+\./.test(hostname) || hostname.endsWith('.vercel.app')) return null;
  if (REGIONS_WITHOUT_CHUNK_TICKETS.has(getRegion())) return null;

  const baseUrl = getTranscriptionBaseUrl();
  if (!baseUrl) return null;

  try {
    const parsed = new URL(baseUrl);
    const isLoopback = ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname);
    if (
      parsed.username ||
      parsed.password ||
      (parsed.protocol !== 'https:' && !(parsed.protocol === 'http:' && isLoopback))
    ) {
      return null;
    }

    if (isLoopback) {
      parsed.port = '5411';
      return parsed.origin;
    }
    return `${parsed.origin}/transcription/tickets`;
  } catch {
    return null;
  }
}

/**
 * Resolves the transcription service base URL for the current region.
 *
 * Precedence:
 *   1. PR preview (`?transcription=<instance>` on DEV + AU) — the Transcription
 *      dev preview ingress.
 *   2. `NEXT_PUBLIC_TRANSCRIPTION_SERVICE_BASE_URL`, with Azure-region hosts
 *      (`*.api.prod.heidihealth.com`) rewritten to `*.api.heidihealth.com`.
 *
 * Returns null if neither source is configured.
 */
export function getTranscriptionBaseUrl(fallbackRegion?: Region): string | null {
  if (isOnPrem && typeof window !== 'undefined') {
    return `${window.location.origin}/transcription/main/v1`;
  }

  const environment = getEnvironment();
  // Staging runs the AU and US AWS stacks and the AU_AZ1 Azure stack
  // (au-az1.api.staging.heidihealth.com/transcription/main); regions without
  // their own staging stack fall back to AU.
  const resolvedRegion = getRegion(fallbackRegion);
  const region =
    environment === ENV_NAME.STAGING && !STAGING_REGIONS.has(resolvedRegion)
      ? Region.AU
      : resolvedRegion;

  const previewInstance = getTranscriptionPreviewParam(region, environment);
  if (previewInstance) {
    // Swap the host and instance segment of the configured URL, keeping its
    // service prefix and version; falls back to a hardcoded shape when the env
    // var is unset (local dev only).
    if (transcriptionBaseUrl) {
      const subdomain = regionToApiSubdomain[region];
      const resolved = transcriptionBaseUrl
        .replace('api.aws-<REGION>', subdomain)
        .replace(/^https?:\/\/[^/]+/, `https://${TRANSCRIPTION_PREVIEW_HOSTNAME}`)
        .replace('/transcription/main/', `/transcription/${previewInstance}/`);
      return handleDomainRewrite(resolved);
    }
    return handleDomainRewrite(
      `https://${TRANSCRIPTION_PREVIEW_HOSTNAME}/transcription/${previewInstance}/v1`
    );
  }

  let result: string | undefined;

  if (transcriptionBaseUrl?.includes('api.aws-<REGION>')) {
    const subdomain = regionToApiSubdomain[region];
    result = transcriptionBaseUrl.replace('api.aws-<REGION>', subdomain);
  } else {
    result = transcriptionBaseUrl;
  }

  if (result) {
    return handleDomainRewrite(normalizeAzureApiProdHost(result));
  }

  return null;
}

/** Same as {@link getTranscriptionBaseUrl} but rewrites a trailing `/v1` segment to `/v2`. */
export function getTranscriptionBaseUrlV2(): string | null {
  const url = getTranscriptionBaseUrl();
  return url ? url.replace(/^(.*)\/v1(?=\/|$)/, '$1/v2') : null;
}

t11nClient.interceptors.request.use((config) => {
  const token = getAuthTokenForHttpClientSync();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const currentUser = getCurrentUser();
  if (currentUser?.country) {
    config.headers['X-Country'] = currentUser.country;
  }

  Object.assign(config.headers, getWorkspaceIdHeader());

  applyXClientHeaders(config.headers);
  applyTracingHeaders(config.headers);

  const resolvedUrl = getTranscriptionBaseUrl();
  if (resolvedUrl) {
    config.baseURL = resolvedUrl;
  }

  return config;
});

t11nClientV2.interceptors.request.use((config) => {
  const token = getAuthTokenForHttpClientSync();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const currentUser = getCurrentUser();
  if (currentUser?.country) {
    config.headers['X-Country'] = currentUser.country;
  }

  Object.assign(config.headers, getWorkspaceIdHeader());

  applyXClientHeaders(config.headers);
  applyTracingHeaders(config.headers);

  const resolvedUrl = getTranscriptionBaseUrlV2();
  if (resolvedUrl) {
    config.baseURL = resolvedUrl;
  }

  return config;
});

attachConfirmedDesktopOfflineRequestShortCircuit(t11nClient);
attachConfirmedDesktopOfflineRequestShortCircuit(t11nClientV2);

// Callers log these errors, so strip the bearer before they see them.
const redactRejectedHeaders = (error: AxiosError) => {
  redactHeadersInPlace(error?.config?.headers);
  redactHeadersInPlace(error?.response?.config?.headers);
  return Promise.reject(error);
};

t11nClient.interceptors.response.use((response) => response, redactRejectedHeaders);
t11nClientV2.interceptors.response.use((response) => response, redactRejectedHeaders);

export async function patchSpeakerLabels(
  sessionId: string,
  speakerLabels: Record<string, string | null>
): Promise<SpeakerLabelsPatchResponse> {
  const { data } = await t11nClient.patch<unknown>(
    `/${sessionId}/transcript/speaker-labels`,
    { speaker_labels: speakerLabels }
  );
  return SpeakerLabelsPatchResponseSchema.parse(data);
}

export async function patchSpeakerRoles(
  sessionId: string,
  speakerRoles: Record<string, SpeakerRole | null>
): Promise<SpeakerRolesPatchResponse> {
  const { data } = await t11nClient.patch<unknown>(
    `/${sessionId}/transcript/speaker-roles`,
    { speaker_roles: speakerRoles }
  );
  return SpeakerRolesPatchResponseSchema.parse(data);
}

export async function patchSegmentSpeakers(
  sessionId: string,
  reassignments: SegmentReassignment[]
): Promise<SegmentSpeakersPatchResponse> {
  const { data } = await t11nClient.patch<unknown>(
    `/${sessionId}/transcript/segment-speakers`,
    { reassignments }
  );
  return SegmentSpeakersPatchResponseSchema.parse(data);
}

export async function createSpeaker(
  sessionId: string,
  body: CreateSpeakerBody = {}
): Promise<SpeakerResponse> {
  const { data } = await t11nClient.post<unknown>(
    `/${sessionId}/transcript/speaker-roster`,
    body
  );
  return SpeakerResponseSchema.parse(data);
}

/**
 * Registers a v2 recording on the transcription service. The client-generated
 * recording id is also the server id, and this must precede any chunk upload.
 *
 * Retries transient failures (network / 5xx / 408 / 429) up to 3 times; other
 * 4xx and `ERR_CANCELED` short-circuit as `AbortError`.
 */
export async function registerV2Recording(
  sessionId: string,
  recordingId: string,
  languages: string[],
  signal?: AbortSignal
): Promise<void> {
  await pRetry(
    async () => {
      try {
        await t11nClient.put(
          `/${sessionId}/recordings/${recordingId}`,
          { languages },
          { signal }
        );
      } catch (err) {
        if (!isRetryableRegisterError(err)) {
          throw new AbortError(err instanceof Error ? err : new Error(String(err)));
        }
        throw err;
      }
    },
    { retries: 3, minTimeout: 500, maxTimeout: 1000 }
  );
}

/** Network / 5xx / 408 / 429 → retryable. Other 4xx → fail fast. */
function isRetryableRegisterError(err: unknown): boolean {
  if (!(err instanceof AxiosError)) return false;
  if (err.code === 'ERR_CANCELED') return false;
  const status = err.response?.status;
  if (status === undefined) return true; // network / DNS / timeout
  if (status >= 500) return true;
  if (status === 408 || status === 429) return true;
  return false;
}

/**
 * Uploads a single chunk to the v2 transcription service. `duration` is in
 * milliseconds here; v1 uses seconds.
 *
 * A 422 / FileTooSmall is not an error — the chunk is dropped and not retried.
 * The discriminated outcome separates that from an accepted chunk, so a caller
 * cannot log a dropped one as `completed`.
 */
export async function uploadV2Chunk(params: {
  sessionId: string;
  recordingId: string;
  blob: Blob;
  index: number;
  durationMs: number;
  fileName: string;
}): Promise<'uploaded' | 'dropped_too_small'> {
  const { sessionId, recordingId, blob, index, durationMs, fileName } = params;
  const formData = new FormData();
  formData.append('file', blob, fileName);
  formData.append('chunk_index', String(index));
  formData.append('duration', String(Math.round(durationMs)));
  formData.append('recording_id', recordingId);

  try {
    await t11nClient.post<TranscriptionResult>(
      `/${sessionId}/transcribe-chunk`,
      formData
    );
    return 'uploaded';
  } catch (err) {
    // Mirror `uploadChunk.ts:20-32`'s 422 / FileTooSmall handling so v2
    // desktop chunks that are too short get the same silent drop as v1.
    if (err instanceof AxiosError) {
      const status = err.response?.status;
      const code = (err.response?.data as { code?: string } | undefined)?.code;
      if (status === 422 || code === 'FileTooSmall') {
        // The swallow path drops any 422, not only too-short audio, so
        // deterministic 422s would lose a whole recording silently. Warn so ops
        // can alert on a run of them.
        //
        // Truncated and never PHI: the 422 body is a small
        // `{detail|code|message}` shape.
        const responseData: unknown = err.response?.data;
        const bodySnippet =
          typeof responseData === 'string'
            ? responseData.slice(0, 200)
            : JSON.stringify(responseData ?? null).slice(0, 200);
        logger.warn({
          tag: 'v2_chunk_upload_422_swallowed',
          event_type: 'V2_CHUNK_UPLOAD_422_SWALLOWED',
          details: {
            status,
            code,
            session_id: sessionId,
            recording_id: recordingId,
            chunk_index: index,
            body_snippet: bodySnippet,
          },
        });
        return 'dropped_too_small';
      }
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Dictation log-CRUD lives in `packages/dictate/src/data/api/logs.ts` and is
// re-exported here name-intact, so importers and `jest.mock` literals keep
// resolving through this path. Those bodies talk to THIS client, which the
// package receives as a LOADER — a static hand-off would drag this module's
// import cone into `_app` module scope and break page-data collection.
// ---------------------------------------------------------------------------
export {
  deleteDictation,
  deleteDictations,
  DictationResponseSchema,
  DictationsResponseSchema,
  DictationStatsResponseSchema,
  getDictations,
  getDictationStats,
  searchDictations,
} from '@heidi/dictate/data';
export type {
  DictationPercentileBand,
  DictationsResponse,
  DictationStatsResponse,
  GetDictationsParams,
  SearchDictationsParams,
} from '@heidi/dictate/data';
